import React from "react";
import {
  Badge,
  type BadgeProps,
  Button,
  Card,
  CardFooter,
  CardHeader,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import { LockClosed20Regular } from "@fluentui/react-icons/svg/lock-closed";
import { useTranslation } from "react-i18next";
import { useConfig } from "@contexts/ConfigContext";
import type { Source, SourceBillingBadge } from "@onetasklist/shared";
import { formatBillingDate } from "@features/billing/formatBillingDate";

const BILLING_BADGE_STYLES: Record<SourceBillingBadge["kind"], Pick<BadgeProps, "appearance" | "color">> = {
  free: { appearance: "tint", color: "success" },
  trial: { appearance: "tint", color: "brand" },
  trialAvailable: { appearance: "tint", color: "brand" },
  upgrade: { appearance: "outline", color: "informative" },
};

interface CatalogueSourceCardProps {
  source: Source;
  isConnecting: boolean;
  hasVoted: boolean;
  onConnect: () => void;
  onVote: () => void;
  onOpenPrivacy: () => void;
  /** The source's tier or trial under the user's plan; none when billing is off. */
  billingBadge?: SourceBillingBadge | null;
}

export const CatalogueSourceCard: React.FC<CatalogueSourceCardProps> = ({
  source,
  isConnecting,
  hasVoted,
  onConnect,
  onVote,
  onOpenPrivacy,
  billingBadge,
}) => {
  const { t, i18n } = useTranslation();
  const { config } = useConfig();
  const iconBaseUrl = config?.regionBaseUrl || "";

  return (
    <Card className="catalogue-source-card">
      <CardHeader
        image={
          <img
            className="catalogue-source-card__icon"
            src={`${iconBaseUrl}/assets/img/sourceicons/${source.id.toLowerCase()}.png`}
            alt=""
          />
        }
        header={
          <Text weight="semibold" className="catalogue-source-card__name">
            {source.displayName}
          </Text>
        }
      />

      <Text className="catalogue-source-card__description">
        {source.description}
      </Text>

      <div className="catalogue-source-card__badges">
        {source.category && (
          <Badge appearance="tint" className="catalogue-source-card__category">
            {source.category}
          </Badge>
        )}
        {source.alpha && (
          <Badge
            appearance="filled"
            color="success"
            size="medium"
            className="catalogue-source-card__category"
          >
            {t("board.connections.catalogue.configure.alphaBadge")}
          </Badge>
        )}
        {billingBadge && (
          <Badge
            {...BILLING_BADGE_STYLES[billingBadge.kind]}
            className="catalogue-source-card__category"
          >
            {billingBadge.kind === "trial"
              ? t("billing.badges.trial", { date: formatBillingDate(billingBadge.endsAt, i18n.language) })
              : t(`billing.badges.${billingBadge.kind}`)}
          </Badge>
        )}
      </div>

      <CardFooter className="catalogue-source-card__footer">
        {source.backlog ? (
          <Button
            appearance={hasVoted ? "secondary" : "outline"}
            size="small"
            disabled={hasVoted}
            onClick={onVote}
          >
            {hasVoted
              ? t("board.connections.catalogue.voted")
              : t("board.connections.catalogue.vote")}
          </Button>
        ) : (
          <Button
            appearance="outline"
            size="small"
            disabled={isConnecting}
            onClick={onConnect}
          >
            {t("board.connections.catalogue.connect")}
          </Button>
        )}

        {source.securityDescription && (
          <Tooltip
            content={t("board.connections.catalogue.privacyButtonAria", {
              name: source.displayName,
            })}
            relationship="label"
          >
            <Button
              appearance="subtle"
              size="small"
              icon={<LockClosed20Regular />}
              onClick={onOpenPrivacy}
              aria-label={t("board.connections.catalogue.privacyButtonAria", {
                name: source.displayName,
              })}
            />
          </Tooltip>
        )}
      </CardFooter>
    </Card>
  );
};
